// THE WARM QUICK-3D PAGE (internal, 2026-10-21 — « Régénérer » → 3D in seconds).
//
// The VPS render runner (render-runner/quick.js) opens this page ONCE and keeps
// it open. It long-polls the worker for quick jobs and hands each one to
// `window.__storizQuick.start(job)`; the page redraws only the pages that
// changed (lib/quickPreview.ts drawQuickBook — the print's own drawing code),
// uploads the new pictures, and leaves the manifest on `jobs[id].result` for
// the runner to give the worker, which publishes it (compare-and-swap).
//
// Why a page that stays open: a fresh browser costs 8–12 s before it draws
// anything (measured on Cloudflare, 2026-10-21) — the whole budget. Open, it
// also keeps what does not change between two clicks: the app bundle, fonts,
// pdf.js, the covers list and settings, the décor pieces and the album's
// photos (pdfGenerator's render cache, keyed by file — same bytes, no download).
//
// Every read the runner makes is one short page.evaluate (never one long CDP
// call — the 180 s protocol lesson, project_storiz_protocol_timeout).
//
// Gated by the same probe token as /_render-spike. Not linked from any UI.
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { coversApi, settingsApi, type Cover } from '@/lib/api';
import { setRenderBlobCache } from '@/lib/pdfGenerator';
import { applyRenderObjectCache } from '@/lib/renderObjectCacheSwitch';
import { makeByteLru, type RenderBlobCache } from '@/lib/renderCache';
import { asManifest, drawQuickBook, type QuickProgress } from '@/lib/quickPreview';

const PROBE = 'sz-bp-9f2c7a1e8b3d4e';
/** Covers + settings are re-read after this long (a new cover, a changed setting). */
const CATALOG_TTL_MS = 5 * 60_000;
/** The cache never grows past this (the container has 4 GB; Chromium needs room to draw). */
const BLOB_CAP_BYTES = 350 * 1024 * 1024;
const NORM_CAP_BYTES = 200 * 1024 * 1024;

interface QuickJobIn {
  jobId: number;
  shopifyOrderId: string;
  albumIndex: number;
  stored: unknown;
}
interface QuickJobState {
  done: boolean;
  ok?: boolean;
  progress: QuickProgress;
  result?: {
    manifest: unknown | null; baseGeneratedAt: string | null; mode: string | null;
    pages: number; cover: boolean; skipped: string | null; ms: number; timings?: Record<string, number>;
  };
  error?: string;
  startedAt: number;
}

export default function QuickRenderProbe() {
  const [sp] = useSearchParams();
  const ran = useRef(false);
  const [msg, setMsg] = useState('starting…');

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const W = window as any;
    if (sp.get('probe') !== PROBE) { W.__storizQuick = { ready: false, error: 'forbidden' }; setMsg('forbidden'); return; }

    const blobs = makeByteLru<Blob>(BLOB_CAP_BYTES, (b) => b.size);
    const norms = makeByteLru<{ bytes: Uint8Array; mime: 'image/jpeg' | 'image/png' }>(NORM_CAP_BYTES, (v) => v.bytes.byteLength);
    const cache: RenderBlobCache = {
      get: (k) => blobs.get(k), set: (k, b) => blobs.set(k, b),
      getNorm: (k) => norms.get(k), setNorm: (k, v) => norms.set(k, v),
    };
    setRenderBlobCache(cache);

    let catalog: { covers: Cover[]; settings: Awaited<ReturnType<typeof settingsApi.get>>['settings']; at: number } | null = null;
    const loadCatalog = async () => {
      if (catalog && Date.now() - catalog.at < CATALOG_TTL_MS) return catalog;
      const [{ covers }, { settings }] = await Promise.all([coversApi.list(), settingsApi.get()]);
      catalog = { covers, settings, at: Date.now() };
      // Download originals through the worker's R2 copy when ticked (migration 269).
      applyRenderObjectCache(settings);
      return catalog;
    };

    const jobs: Record<string, QuickJobState> = {};
    let running = 0;
    const api = {
      ready: false,
      jobs,
      cache: () => ({ blobs: blobs.size, blobMb: Math.round(blobs.bytes / 1048576), norms: norms.size, normMb: Math.round(norms.bytes / 1048576) }),
      busy: () => running,
      /** Start a job; returns at once. The runner then polls `jobs[jobId]`. */
      start(job: QuickJobIn): boolean {
        const id = String(job.jobId);
        if (jobs[id]) return false;
        const st: QuickJobState = { done: false, progress: { stage: 'load', done: 0, total: 0 }, startedAt: Date.now() };
        jobs[id] = st;
        // Keep the record small: only the last 20 jobs.
        const ids = Object.keys(jobs);
        if (ids.length > 20) for (const k of ids.slice(0, ids.length - 20)) if (jobs[k].done) delete jobs[k];
        running++;
        setMsg(`job ${id} — ${job.shopifyOrderId} album ${job.albumIndex}`);
        void (async () => {
          try {
            const stored = asManifest(job.stored);
            if (!stored) throw new Error('no stored manifest in the job');
            const cat = await loadCatalog();
            const book = await drawQuickBook({
              shopifyOrderId: job.shopifyOrderId,
              albumIndex: job.albumIndex,
              stored,
              covers: cat.covers,
              settings: cat.settings,
              onProgress: (p) => { st.progress = p; },
            });
            st.result = {
              manifest: book.manifest, baseGeneratedAt: stored.generated_at, mode: book.mode ?? null,
              pages: book.pages, cover: book.cover, skipped: book.skipped ?? null, ms: Date.now() - st.startedAt, timings: book.timings,
            };
            st.ok = true;
          } catch (e) {
            st.ok = false;
            st.error = e instanceof Error ? (e.stack ?? e.message).slice(0, 800) : String(e);
            console.error('[quick-probe] job failed', id, st.error);
          } finally {
            st.progress = { ...st.progress, stage: 'done' };
            st.done = true;
            running--;
            setMsg(`idle — last job ${id} ${st.ok ? 'ok' : 'failed'} in ${Date.now() - st.startedAt} ms`);
          }
        })();
        return true;
      },
    };
    W.__storizQuick = api;

    // Warm everything a first job would otherwise pay for, then say ready.
    (async () => {
      try {
        await loadCatalog();
        try { await (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts?.ready; } catch { /* ignore */ }
        // pdf.js and the generator chunks, loaded once.
        await Promise.all([import('@/lib/previewAssets').then((m) => m.loadPdfjs()), import('@/lib/pdfGenerator')]);
      } catch (e) {
        console.warn('[quick-probe] warm-up incomplete (jobs will load what they need):', e);
      }
      api.ready = true;
      setMsg('ready — waiting for jobs');
    })();
  }, [sp]);

  return <div style={{ padding: 20, fontFamily: 'sans-serif' }}>Quick 3D — {msg}</div>;
}
